import { useEffect, useRef, useState } from 'react';

export const heroTaglines = [
  '160课时 · 一人一剧 · 从题材、剧本、分镜、AI制作，到完整成片与真实发行运营。',
  '从第一个故事想法，到自己的完整作品，把每一步学习都做进真实项目。',
] as const;

type Frame = { line: number; length: number; phase: 'start' | 'typing' | 'holding' | 'erasing' | 'gap' };

// Match the reference's Chinese typing rhythm; keep both lines in a hidden
// sizing layer so the heading and surrounding posters never jump between lines.
export default function HeroTagline() {
  const root = useRef<HTMLParagraphElement>(null);
  const [active, setActive] = useState(false);
  const [reduced, setReduced] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const [frame, setFrame] = useState<Frame>({ line: 0, length: 0, phase: 'start' });

  useEffect(() => {
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    let inView = false;
    const refresh = () => { setActive(inView && !document.hidden); setReduced(preference.matches); };
    const observer = new IntersectionObserver(([entry]) => { inView = entry.isIntersecting; refresh(); }, { rootMargin: '120px 0px', threshold: .01 });
    if (root.current) observer.observe(root.current);
    document.addEventListener('visibilitychange', refresh);
    preference.addEventListener('change', refresh);
    return () => { observer.disconnect(); document.removeEventListener('visibilitychange', refresh); preference.removeEventListener('change', refresh); };
  }, []);

  useEffect(() => {
    if (!active || reduced) return;
    const delay = frame.phase === 'start' ? 280 : frame.phase === 'holding' ? 1000 : frame.phase === 'erasing' ? 8 : frame.phase === 'gap' ? 180 : frame.line === 0 ? 70 : 82;
    const timer = window.setTimeout(() => setFrame(current => {
      if (current.phase === 'start') return { line: 0, length: 1, phase: 'typing' };
      if (current.phase === 'gap') return { line: (current.line + 1) % heroTaglines.length, length: 1, phase: 'typing' };
      if (current.phase === 'holding') return { ...current, length: current.length - 1, phase: 'erasing' };
      if (current.phase === 'erasing') return { ...current, length: current.length - 1, phase: current.length === 1 ? 'gap' : 'erasing' };
      const length = current.length + 1;
      return { ...current, length, phase: length === heroTaglines[current.line].length ? 'holding' : 'typing' };
    }), delay);
    return () => window.clearTimeout(timer);
  }, [active, reduced, frame]);

  return <p className="experience-subtitle" ref={root} data-typewriter-line={reduced ? 0 : frame.line} data-typewriter-phase={reduced ? 'static' : frame.phase}>
    <span className="sr-only">{heroTaglines.join(' ')}</span>
    <span className="experience-tagline-ghost" aria-hidden="true">{heroTaglines.map(line => <span key={line}>{line}</span>)}</span>
    <span className="experience-tagline-live" aria-hidden="true">{reduced ? heroTaglines[0] : heroTaglines[frame.line].slice(0, frame.length)}{!reduced && <i className="experience-typewriter-cursor" />}</span>
  </p>;
}
